import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { getAvailableWordPacks } from '../data/wordpacks';
import type { WordPack } from '../types/GameTypes';
import './Settings.css';

interface SettingsProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  onDarkModeToggle: () => void;
  selectedWordPack: string;
  onSelectedWordPackChange: (packId: string) => void;
  isRstlneMode: boolean;
  onRstlneModeToggle: () => void;
  animationsEnabled: boolean;
  onAnimationsToggle: () => void;
  gameMode: 'daily' | 'free-play';
}

const Settings: React.FC<SettingsProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  onDarkModeToggle,
  selectedWordPack,
  onSelectedWordPackChange,
  isRstlneMode,
  onRstlneModeToggle,
  animationsEnabled,
  onAnimationsToggle,
  gameMode
}) => {
  const [availableWordPacks, setAvailableWordPacks] = useState<Record<string, WordPack>>({});
  const [isLoadingPacks, setIsLoadingPacks] = useState(true);

  // Load available word packs on mount
  useEffect(() => {
    const loadWordPacks = async () => {
      try {
        setIsLoadingPacks(true);
        const packs = await getAvailableWordPacks();
        setAvailableWordPacks(packs);
      } catch (error) {
        console.error('Failed to load word packs:', error);
        setAvailableWordPacks({});
      } finally {
        setIsLoadingPacks(false);
      }
    };

    loadWordPacks();
  }, []);

  const handleWordPackChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onSelectedWordPackChange(e.target.value);
  };

  const handleRSTLNEModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onRstlneModeToggle();
  };

  const handleDarkModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onDarkModeToggle();
  };

  const handleAnimationsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onAnimationsToggle();
  };

  return (
    <Modal title="Settings" isOpen={isOpen} onClose={onClose}>
      <div className="settings-body">
        <div className="setting-group">
          <label className="setting-label">
            <input
              type="checkbox"
              checked={isDarkMode}
              onChange={handleDarkModeChange}
            />
            Dark Mode
          </label>
        </div>

        <div className="setting-group">
          <label className="setting-label">
            <input
              type="checkbox"
              checked={animationsEnabled}
              onChange={handleAnimationsChange}
            />
            Celebration Animations
          </label>
        </div>

        {gameMode === 'free-play' && (
          <>
            <div className="setting-group">
              <label className="setting-label">Word Pack:</label>
              <select 
                value={selectedWordPack} 
                onChange={handleWordPackChange}
                className="setting-select"
                disabled={isLoadingPacks}
              >
                {isLoadingPacks ? (
                  <option value="loading" disabled>Loading word packs...</option>
                ) : (
                  Object.entries(availableWordPacks).map(([packId, pack]) => (
                    <option key={packId} value={packId}>
                      {pack.name} ({pack.words.length} words)
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="setting-group">
              <label className="setting-label">
                <input
                  type="checkbox"
                  checked={isRstlneMode}
                  onChange={handleRSTLNEModeChange}
                />
                RSTLNE Mode (show common letters R, S, T, L, N, E)
              </label>
            </div>
          </>
        )}

        {gameMode === 'daily' && (
          <div className="setting-note">
            <p>Word length and RSTLNE mode are automatically set for daily challenges.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default Settings;
